
import React from 'react';
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { ALMContract } from './types';
import { formatCurrency } from '@/components/borrowings/debt/utils';

interface ContractResetsProps {
  contract: ALMContract;
}

const ContractResets: React.FC<ContractResetsProps> = ({ contract }) => {
  return (
    <div className="space-y-4">
      {contract.interestType === "Floating" && (
        <Card>
          <CardContent className="pt-6">
            <CardTitle className="text-base mb-2">Interest Rate Details</CardTitle>
            <CardDescription>Floating rate specifications</CardDescription>
            
            <div className="mt-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Reference Rate</span>
                <span className="text-sm font-medium">{contract.referenceRate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Current Reference Value</span>
                <span className="text-sm font-medium">{contract.currentReferenceValue}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Spread</span>
                <span className="text-sm font-medium">{contract.spread}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Floor Rate</span>
                <span className="text-sm font-medium">{contract.floorRate ? `${contract.floorRate}%` : 'None'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Cap Rate</span>
                <span className="text-sm font-medium">{contract.capRate ? `${contract.capRate}%` : 'None'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardContent className="pt-6">
          <CardTitle className="text-base mb-2">Prepayment Assumptions</CardTitle>
          <CardDescription>Conditions for early repayment</CardDescription>
          
          <div className="mt-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Allowed</span>
              <span className="text-sm">{contract.prepaymentOption ? 'Yes' : 'No'}</span>
            </div>
            {contract.prepaymentOption && (
              <>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Fee Structure</span>
                  <span className="text-sm">{contract.prepaymentFeeStructure || 'None'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Minimum Notice</span>
                  <span className="text-sm">{contract.prepaymentMinimumNotice || 'None'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Minimum Amount</span>
                  <span className="text-sm">{contract.prepaymentMinimumAmount ? formatCurrency(contract.prepaymentMinimumAmount) : 'None'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Lock-in Period</span>
                  <span className="text-sm">{contract.prepaymentLockInPeriod || 'None'}</span>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-6">
          <CardTitle className="text-base mb-2">Risk Parameters</CardTitle>
          <CardDescription>Risk assessment factors</CardDescription>
          
          <div className="mt-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Interest Rate Sensitivity</span>
              <span className={`text-sm ${getRiskColor(contract.interestRateSensitivity as 'low' | 'medium' | 'high')}`}>
                {getRiskLabel(contract.interestRateSensitivity as 'low' | 'medium' | 'high')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Liquidity Risk</span>
              <span className={`text-sm ${getRiskColor(contract.liquidityRisk as 'low' | 'medium' | 'high')}`}>
                {getRiskLabel(contract.liquidityRisk as 'low' | 'medium' | 'high')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Credit Risk</span>
              <span className={`text-sm ${getRiskColor(contract.creditRisk as 'low' | 'medium' | 'high')}`}>
                {getRiskLabel(contract.creditRisk as 'low' | 'medium' | 'high')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const getRiskLabel = (risk?: 'low' | 'medium' | 'high') => {
  if (!risk) return 'Not Assessed';
  return risk.charAt(0).toUpperCase() + risk.slice(1);
};

const getRiskColor = (risk?: 'low' | 'medium' | 'high') => {
  switch (risk) {
    case 'low':
      return 'text-green-600';
    case 'medium':
      return 'text-amber-600';
    case 'high':
      return 'text-red-600';
    default:
      return '';
  }
};

export default ContractResets;
